import type { Vertical } from '@/types/common';

/**
 * Builds a provider URL with UTM and Flexaura referral parameters
 * for commercial partnership attribution.
 */
export function buildReferralUrl(
  providerUrl: string,
  provider: string,
  vertical: Vertical,
  merchantName: string,
  amount: number,
  currency: string
): string {
  const params = new URLSearchParams({
    utm_source: 'flexaura',
    utm_medium: 'marketplace',
    utm_campaign: vertical,
    utm_content: provider,
    utm_term: merchantName.toLowerCase().replace(/\s+/g, '-'),
    ref: 'flexaura',
    fxa_session: generateSessionId(),
    fxa_vertical: vertical,
    fxa_amount: amount.toString(),
    fxa_currency: currency,
    fxa_ts: Date.now().toString(),
  });

  // If providerUrl already has query params, append with &
  const separator = providerUrl.includes('?') ? '&' : '?';
  return `${providerUrl}${separator}${params.toString()}`;
}

export function generateSessionId(): string {
  return 'fxa_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
}

/**
 * Fire-and-forget referral click tracking.
 * Never blocks navigation — errors are silently logged.
 */
export function trackReferralClick(data: {
  sessionId: string;
  provider: string;
  vertical: Vertical;
  merchantName: string;
  amount: number;
  currency: string;
  referrerPage: string;
}): void {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-referral`;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  // Fire and forget — do NOT await
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${key}`,
      apikey: key,
    },
    body: JSON.stringify({
      session_id: data.sessionId,
      provider: data.provider,
      vertical: data.vertical,
      merchant_name: data.merchantName,
      intended_amount: data.amount,
      currency: data.currency,
      referrer_page: data.referrerPage,
    }),
  }).catch((err) => {
    console.warn('[referral-tracking] fire-and-forget failed:', err);
  });
}
